import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserPlus, CheckCircle, Info } from 'lucide-react';

import UserContext from 'context/userContext';
import { Button } from '@epoch-ui/components/Button';
import {
  Form,
  ButtonGroupField,
  ButtonGroupFieldItem,
  SwitchField,
  ActionButtonGroupField,
  ActionButtonGroupItem,
  ActionButtonGroupItemIcon,
  ActionButtonGroupItemLabel,
  ActionButtonGroupItemCaption
} from '@epoch-ui/components/Form';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { EventUser } from 'types/event.types';

import {
  CalendarEventFormValidationSchema,
  type CalendarEventFormValues
} from './calendarSettings.schema';

export interface CalendarSettingsModalProps extends React.ComponentPropsWithoutRef<typeof Modal> {
  initialValues: Partial<CalendarEventFormValues>;
  creatorDetails?: EventUser;
  onSubmit: (values: CalendarEventFormValues) => void;
  disabled?: boolean;
  /** Set when the control that opens this modal is already flagging an error, so the
   *  modal surfaces the same reason instead of looking settled. */
  showValidationOnOpen?: boolean;
}

export const CalendarSettingsModal = React.memo<CalendarSettingsModalProps>(
  ({
    open,
    onOpenChange,
    initialValues,
    creatorDetails,
    onSubmit,
    disabled = false,
    showValidationOnOpen = false,
    ...props
  }) => {
    const userContext = useContext(UserContext);
    const { companyUsesOutlookCalendar } = userContext || {};
    const { firstName, lastName } = creatorDetails || userContext || {};
    const form = useForm<CalendarEventFormValues>({
      resolver: zodResolver(CalendarEventFormValidationSchema),
      defaultValues: initialValues,
      mode: 'onChange'
    });

    const { watch } = form;

    const localCalendarNotificationsEnabled = watch('calendarNotifications');

    // Only pre-flag when the caller says its own control is already showing an error —
    // otherwise a first visit to an untouched draft would open onto a red message the
    // admin has done nothing to earn.
    React.useEffect(() => {
      if (open && !disabled && showValidationOnOpen) {
        form.trigger();
      }
    }, [open, disabled, showValidationOnOpen, form]);

    const handleOpenChange = React.useCallback(
      (isOpen: boolean): void => {
        // Reset form when modal is closed
        if (!isOpen) {
          form.reset(initialValues);
        }
        onOpenChange?.(isOpen);
      },
      [initialValues, onOpenChange, form]
    );

    return (
      <Modal open={open} onOpenChange={handleOpenChange} {...props}>
        <Form {...form}>
          <ModalContent size="md">
            <form
              onSubmit={(e) => {
                e.stopPropagation();
                form.handleSubmit((data) => {
                  onSubmit(data);
                  onOpenChange?.(false);
                })(e);
              }}
            >
              <ModalHeader className="tw-mb-2">
                <ModalTitle className="tw-text-2xl tw-font-medium">Calendar</ModalTitle>
              </ModalHeader>
              <ModalDescription className="tw-mb-5 tw-text-sm tw-text-muted-foreground">
                Create an event in{' '}
                {companyUsesOutlookCalendar ? 'Outlook Calendar' : 'Google Calendar'} that syncs
                event information, guests, and RSVPs with Epoch.
              </ModalDescription>

              <div className="">
                <SwitchField
                  name="calendarNotifications"
                  label="Create calendar event"
                  description={`${firstName} ${lastName}'s calendar`}
                  aria-label="Create calendar event toggle"
                  className="tw-mb-4 tw-flex-row-reverse tw-justify-between"
                  disabled={disabled}
                />

                {localCalendarNotificationsEnabled && (
                  <div className="">
                    <div className="tw-mb-4">
                      <ActionButtonGroupField
                        name="calendarAddGuestsSetting"
                        label="Add guests to calendar when they are:"
                        className="tw-w-full"
                        disabled={disabled}
                      >
                        <ActionButtonGroupItem value="invited">
                          <ActionButtonGroupItemIcon>
                            <UserPlus size={24} />
                          </ActionButtonGroupItemIcon>
                          <ActionButtonGroupItemLabel>
                            Invited
                            <ActionButtonGroupItemCaption>
                              Guests are added upon invitation to the event
                            </ActionButtonGroupItemCaption>
                          </ActionButtonGroupItemLabel>
                        </ActionButtonGroupItem>
                        <ActionButtonGroupItem value="rsvped">
                          <ActionButtonGroupItemIcon>
                            <CheckCircle size={24} />
                          </ActionButtonGroupItemIcon>
                          <ActionButtonGroupItemLabel>
                            RSVPed
                            <ActionButtonGroupItemCaption>
                              Guests are added after they RSVP to the event
                            </ActionButtonGroupItemCaption>
                          </ActionButtonGroupItemLabel>
                        </ActionButtonGroupItem>
                      </ActionButtonGroupField>
                    </div>

                    <div className="tw-mb-5">
                      <ButtonGroupField
                        name="calendarEventVisibility"
                        label={
                          <span className="tw-mb-2 tw-text-sm tw-font-medium">
                            Set calendar event visibility to:
                          </span>
                        }
                        className="tw-w-full"
                        disabled={disabled}
                      >
                        <ButtonGroupFieldItem value="busy">Busy</ButtonGroupFieldItem>
                        <ButtonGroupFieldItem value="free">Free</ButtonGroupFieldItem>
                      </ButtonGroupField>
                    </div>
                  </div>
                )}

                <div className="tw-flex tw-align-middle">
                  <Info size={16} className="tw-mr-1 tw-text-medium-grey" />
                  <span className="tw-text-medium-grey">
                    Settings can&apos;t be changed after an event is published.
                  </span>
                </div>
              </div>
              {!disabled && (
                <ModalFooter className="tw-mt-5">
                  <Button
                    size="lg"
                    className="tw-h-[40px] tw-w-full tw-rounded-lg"
                    type="submit"
                    variant="primary"
                  >
                    Save
                  </Button>
                </ModalFooter>
              )}
            </form>
          </ModalContent>
        </Form>
      </Modal>
    );
  }
);
